import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
  type QueryClient,
} from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  getActivityFeed,
  getActivityFeedCounts,
  getInfiniteActivityFeed,
  markAllActivityFeedRead,
  markActivityFeedRead,
  markActivityFeedUnread,
  markActivityFeedViewed,
  type ActivityFeedIndexKeyEntries,
  type ActivityFeedTarget,
} from './activity-feed.api'

import type {
  ActivityFeedCounts,
  ActivityFeedFilters,
  ActivityFeedReadState,
} from './activity-feed.types'

interface OrganizationScopeInput {
  organizationId?: OrganizationIdInput
}

interface ActivityFeedQueryInput extends OrganizationScopeInput {
  filters?: ActivityFeedFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

type ActivityFeedQueryKey = [AdminQueryRoot & ActivityFeedIndexKeyEntries]
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const activityFeedQueryKeys = {
  all: ({ organizationId }: OrganizationScopeInput = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'activityFeed' }, { organizationId }),
  ],
  index: ({
    organizationId,
    filters,
    page,
    pageSize,
  }: ActivityFeedQueryInput = {}): ActivityFeedQueryKey =>
    buildScopedAdminQueryKey(activityFeedQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
      page,
      pageSize,
    }),
  counts: ({ organizationId, filters }: ActivityFeedQueryInput = {}): ActivityFeedQueryKey =>
    buildScopedAdminQueryKey(activityFeedQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'counts',
      filters,
    }),
  infinite: ({
    organizationId,
    filters,
    pageSize,
  }: ActivityFeedQueryInput = {}): ActivityFeedQueryKey =>
    buildScopedAdminQueryKey(activityFeedQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'infinite',
      filters,
      pageSize,
    }),
}

export const activityFeedQueries = {
  index: ({ organizationId, filters, page, pageSize }: ActivityFeedQueryInput = {}) =>
    queryOptions({
      queryKey: activityFeedQueryKeys.index({ organizationId, filters, page, pageSize }),
      queryFn: getActivityFeed,
    }),
  counts: ({ organizationId, filters }: ActivityFeedQueryInput = {}) =>
    queryOptions({
      queryKey: activityFeedQueryKeys.counts({ organizationId, filters }),
      queryFn: getActivityFeedCounts,
    }),
  infinite: ({ organizationId, filters, pageSize }: ActivityFeedQueryInput = {}) =>
    infiniteQueryOptions({
      queryKey: activityFeedQueryKeys.infinite({ organizationId, filters, pageSize }),
      queryFn: getInfiniteActivityFeed,
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const currentPage = Number(lastPage?.meta?.current_page ?? 1)
        const lastPageNumber = Number(lastPage?.meta?.last_page ?? 1)

        return currentPage < lastPageNumber ? currentPage + 1 : undefined
      },
    }),
}

function normalizeAffectedOrganizationIds(
  currentOrganizationId: OrganizationIdInput,
  affectedOrganizationIds: OrganizationIdInput[] = []
) {
  const organizationIds = new Set<number>()

  const addOrganizationId = (value: OrganizationIdInput) => {
    const numericId = Number(value)

    if (Number.isInteger(numericId) && numericId > 0) {
      organizationIds.add(numericId)
    }
  }

  addOrganizationId(currentOrganizationId)
  affectedOrganizationIds.forEach(addOrganizationId)

  return [...organizationIds]
}

async function invalidateAffectedActivityFeedCountQueries(
  queryClient: QueryClient,
  {
    currentOrganizationId,
    affectedOrganizationIds = [],
  }: {
    currentOrganizationId: number
    affectedOrganizationIds?: OrganizationIdInput[]
  }
) {
  const organizationIds = normalizeAffectedOrganizationIds(
    undefined,
    affectedOrganizationIds
  ).filter((organizationId) => organizationId !== Number(currentOrganizationId))

  return Promise.all(
    organizationIds.map((organizationId) =>
      queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.counts({ organizationId }),
      })
    )
  )
}

export function useActivityFeedCounts({
  organizationId,
  filters,
  ...params
}: ActivityFeedQueryInput & QueryParams<ReturnType<typeof activityFeedQueries.counts>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...activityFeedQueries.counts({
      organizationId: resolvedOrganizationId,
      filters,
    }),
    ...params,
  })
}

export function useInfiniteActivityFeed({
  organizationId,
  filters,
  ...params
}: ActivityFeedQueryInput & QueryParams<ReturnType<typeof activityFeedQueries.infinite>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useInfiniteQuery({
    ...activityFeedQueries.infinite({
      organizationId: resolvedOrganizationId,
      filters,
      pageSize: params.pageSize,
    }),
    ...params,
  })
}

export function useMarkActivityFeedViewedMutation({
  organizationId: scopedOrganizationId,
}: OrganizationScopeInput = {}) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId(scopedOrganizationId)
  const activityFeedCountsQueryKey = activityFeedQueryKeys.counts({ organizationId })

  return useMutation({
    mutationFn: () => markActivityFeedViewed({ organizationId }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: activityFeedCountsQueryKey })

      const previousCounts = queryClient.getQueryData<ActivityFeedCounts>(
        activityFeedCountsQueryKey
      )

      queryClient.setQueryData<ActivityFeedCounts>(activityFeedCountsQueryKey, (currentCounts) => {
        if (!currentCounts) {
          return currentCounts
        }

        return {
          ...currentCounts,
          new_count: 0,
        }
      })

      return { previousCounts }
    },
    onError: (_, __, context) => {
      if (!context) {
        return
      }

      queryClient.setQueryData(activityFeedCountsQueryKey, context.previousCounts)
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return invalidateAffectedActivityFeedCountQueries(queryClient, {
        currentOrganizationId: organizationId,
        affectedOrganizationIds: Object.keys(response?.previous_viewed_at_by_organization ?? {}),
      })
    },
  })
}

export function useMarkAllActivityFeedReadMutation({
  organizationId: scopedOrganizationId,
}: OrganizationScopeInput = {}) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId(scopedOrganizationId)

  return useMutation({
    mutationFn: () => markAllActivityFeedRead({ organizationId }),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return invalidateAffectedActivityFeedCountQueries(queryClient, {
        currentOrganizationId: organizationId,
        affectedOrganizationIds: response?.affected_organization_ids ?? [],
      })
    },
  })
}

function useActivityFeedReadStateMutation(
  mutationFn: (target: ActivityFeedTarget) => Promise<ActivityFeedReadState>,
  { organizationId: scopedOrganizationId }: OrganizationScopeInput = {}
) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId(scopedOrganizationId)

  return useMutation({
    mutationFn: ({ type, sourceId }: Omit<ActivityFeedTarget, 'organizationId'>) =>
      mutationFn({ organizationId, type, sourceId }),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return invalidateAffectedActivityFeedCountQueries(queryClient, {
        currentOrganizationId: organizationId,
        affectedOrganizationIds: [response?.organization_id].filter(Boolean),
      })
    },
  })
}

export function useMarkActivityFeedReadMutation(options: OrganizationScopeInput = {}) {
  return useActivityFeedReadStateMutation(markActivityFeedRead, options)
}

export function useMarkActivityFeedUnreadMutation(options: OrganizationScopeInput = {}) {
  return useActivityFeedReadStateMutation(markActivityFeedUnread, options)
}
